import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { propertiesAPI, bookingsAPI, availabilityAPI, icalSyncAPI } from '@/lib/api';
import { useDateFormat } from '@/context/DateFormatContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChevronLeft, ChevronRight, Loader2, RefreshCw, Download, Upload, Link as LinkIcon, Copy, Check, Plus, Trash2, RotateCw, AlertCircle, CheckCircle2, Clock, Edit, XCircle, X, CreditCard, Calendar, User, Phone, Mail, DollarSign, LogIn, LogOut, Banknote, ChevronDown, ChevronUp } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CHANNELS = ['direct', 'airbnb', 'booking.com', 'agoda', 'hotels.com', 'expedia', 'vrbo', 'iStayBnB'];
const STATUSES = ['provisional', 'waitlist', 'confirmed', 'checked-in', 'checked-out', 'cancelled'];
const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'mpesa_manual', label: 'M-Pesa (Manual)' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const statusStyles = {
  provisional: 'bg-amber-50 text-amber-700 border-amber-200',
  waitlist: 'bg-purple-50 text-purple-700 border-purple-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'checked-in': 'bg-blue-50 text-blue-700 border-blue-200',
  'checked-out': 'bg-gray-50 text-gray-600 border-gray-200',
  cancelled: 'bg-red-50 text-red-600 border-red-200',
};

const CalendarPage = () => {
  const { formatDate } = useDateFormat();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [loadingProps, setLoadingProps] = useState(true);
  const [loadingCal, setLoadingCal] = useState(false);
  const [icalDialogOpen, setIcalDialogOpen] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [feedLoading, setFeedLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [newSourceName, setNewSourceName] = useState('');
  const [newSyncInterval, setNewSyncInterval] = useState(60);
  const [subLoading, setSubLoading] = useState(false);
  const [editingSubId, setEditingSubId] = useState(null);
  const [editInterval, setEditInterval] = useState(60);
  const [syncingId, setSyncingId] = useState(null);
  
  // Booking detail dialog state
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [bookingForm, setBookingForm] = useState({});
  const [savingBooking, setSavingBooking] = useState(false);
  
  // Payment dialog state
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ payment_method: 'cash', payment_ref: '', notes: '' });
  const [recordingPayment, setRecordingPayment] = useState(false);
  
  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  
  // Today's Overview panel state
  const [overviewExpanded, setOverviewExpanded] = useState(true);
  
  // Date range selection for new booking
  const [dateRangeSelection, setDateRangeSelection] = useState({ start: null, end: null });
  const [newBookingDialogOpen, setNewBookingDialogOpen] = useState(false);
  const [newBookingForm, setNewBookingForm] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    guests: 1,
    channel: 'direct',
    status: 'provisional',
    total_amount: 0,
    notes: ''
  });
  const [savingNewBooking, setSavingNewBooking] = useState(false);

  // Compute today's overview data
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  
  const todaysOverview = useMemo(() => {
    const checkIns = bookings.filter(b => 
      b.check_in === todayStr && 
      !['cancelled', 'checked-in', 'checked-out'].includes(b.status)
    );
    const checkOuts = bookings.filter(b => 
      b.check_out === todayStr && 
      b.status === 'checked-in'
    );
    const pendingPayments = bookings.filter(b => 
      ['provisional', 'waitlist'].includes(b.status)
    );
    return { checkIns, checkOuts, pendingPayments };
  }, [bookings, todayStr]);

  useEffect(() => {
    const loadProps = async () => {
      try {
        const resp = await propertiesAPI.list();
        setProperties(resp.data);
        if (resp.data.length > 0) setSelectedProperty(resp.data[0].property_id);
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoadingProps(false);
      }
    };
    loadProps();
  }, []);

  const loadCalendarData = useCallback(async () => {
    if (!selectedProperty) return;
    setLoadingCal(true);
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      const [bResp, aResp] = await Promise.all([
        bookingsAPI.list({ property_id: selectedProperty }),
        availabilityAPI.get(selectedProperty, { month, year })
      ]);
      setBookings(bResp.data);
      setAvailability(aResp.data);
    } catch (err) {
      console.error('Calendar load error:', err);
    } finally {
      setLoadingCal(false);
    }
  }, [selectedProperty, currentDate]);

  useEffect(() => { loadCalendarData(); }, [loadCalendarData]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const monthName = currentDate.toLocaleString('default', { month: 'long' });
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  
  // Second month for dual view
  const nextMonthDate = new Date(year, month + 1, 1);
  const nextYear = nextMonthDate.getFullYear();
  const nextMonth = nextMonthDate.getMonth();
  const nextMonthName = nextMonthDate.toLocaleString('default', { month: 'long' });
  const nextFirstDay = new Date(nextYear, nextMonth, 1).getDay();
  const nextDaysInMonth = new Date(nextYear, nextMonth + 1, 0).getDate();

  const dateMap = useMemo(() => {
    const map = {};
    // Manual availability
    availability.forEach(a => { map[a.date] = { type: 'manual', status: a.status }; });
    // Bookings override - include bookings that span both visible months
    bookings.forEach(b => {
      if (b.status === 'cancelled') return;
      const start = new Date(b.check_in);
      const end = new Date(b.check_out);
      for (let d = new Date(start); d < end; d.setDate(d.getDate() + 1)) {
        const dateStr = d.toISOString().split('T')[0];
        map[dateStr] = {
          type: 'booking',
          status: b.status,
          guestName: b.guest_name,
          checkIn: b.check_in,
          checkOut: b.check_out,
          channel: b.channel,
          bookingId: b.booking_id
        };
      }
    });
    return map;
  }, [bookings, availability]);

  // Handle opening booking detail on double-click
  const handleBookingDoubleClick = (bookingId) => {
    const booking = bookings.find(b => b.booking_id === bookingId);
    if (booking) {
      setSelectedBooking(booking);
      setBookingForm({
        guest_name: booking.guest_name || '',
        guest_email: booking.guest_email || '',
        guest_phone: booking.guest_phone || '',
        check_in: booking.check_in || '',
        check_out: booking.check_out || '',
        num_guests: booking.num_guests || 1,
        total_price: booking.total_price || 0,
        channel: booking.channel || 'direct',
        status: booking.status || 'provisional',
        notes: booking.notes || ''
      });
      setEditMode(false);
      setBookingDialogOpen(true);
    }
  };

  // Handle booking update
  const handleBookingUpdate = async () => {
    if (!selectedBooking) return;
    setSavingBooking(true);
    try {
      await bookingsAPI.update(selectedBooking.booking_id, bookingForm);
      await loadCalendarData();
      setBookingDialogOpen(false);
      setSelectedBooking(null);
      setEditMode(false);
      toast.success('Booking updated successfully');
    } catch (err) {
      console.error('Update error:', err);
      toast.error(err.response?.data?.detail || 'Failed to update booking');
    } finally {
      setSavingBooking(false);
    }
  };

  // Handle manual payment recording
  const handleRecordPayment = async () => {
    if (!selectedBooking) return;
    setRecordingPayment(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/bookings/${selectedBooking.booking_id}/record-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ihostpms_token')}`
        },
        body: JSON.stringify(paymentForm)
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to record payment');
      await loadCalendarData();
      setPaymentDialogOpen(false);
      setBookingDialogOpen(false);
      setPaymentForm({ payment_method: 'cash', payment_ref: '', notes: '' });
      setSelectedBooking(null);
      toast.success(data.message || 'Payment recorded successfully');
    } catch (err) {
      console.error('Payment error:', err);
      toast.error(err.message || 'Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  // Handle booking cancellation
  const handleCancelBooking = async () => {
    if (!selectedBooking) return;
    setCancelling(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/bookings/${selectedBooking.booking_id}/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ihostpms_token')}`
        },
        body: JSON.stringify({ reason: cancelReason })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Failed to cancel booking');
      await loadCalendarData();
      setCancelDialogOpen(false);
      setBookingDialogOpen(false);
      setCancelReason('');
      setSelectedBooking(null);
      toast.success('Booking cancelled successfully');
    } catch (err) {
      console.error('Cancel error:', err);
      toast.error(err.message || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  // Helper to render a single month calendar
  const renderMonth = (monthYear, monthNum, monthLabel, firstDayOfMonth, daysCount) => (
    <div className="flex-1 min-w-0">
      <h3 className="text-lg font-bold text-airbnb-gray text-center mb-3">{monthLabel} {monthYear}</h3>
      <div className="grid grid-cols-7 gap-0.5 lg:gap-1" data-testid={`calendar-grid-${monthNum}`}>
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
          <div key={d} className="text-center text-[10px] lg:text-xs font-semibold text-airbnb-medium-gray uppercase tracking-wider py-1">{d}</div>
        ))}
        {Array.from({ length: firstDayOfMonth }).map((_, i) => <div key={`e-${monthNum}-${i}`} className="aspect-square" />)}
        <TooltipProvider>
          {Array.from({ length: daysCount }).map((_, i) => {
            const day = i + 1;
            const dateStr = `${monthYear}-${String(monthNum + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const info = dateMap[dateStr];
            const isToday = dateStr === new Date().toISOString().split('T')[0];
            const isSelectedStart = dateRangeSelection.start === dateStr;
            const isSelectedEnd = dateRangeSelection.end === dateStr;
            const isInRange = isDateInSelection(dateStr) && !isSelectedStart && !isSelectedEnd;
            return (
              <Tooltip key={`${monthNum}-${day}`}>
                <TooltipTrigger asChild>
                  <button
                    data-testid={`calendar-day-${monthNum}-${day}`}
                    onClick={() => handleDateClickDual(monthYear, monthNum, day)}
                    onDoubleClick={() => info?.type === 'booking' && handleBookingDoubleClick(info.bookingId)}
                    className={`aspect-square flex flex-col items-center justify-center rounded-lg border text-xs lg:text-sm transition-all ${getDateStyle(info, dateStr)} ${isToday && !isSelectedStart && !isSelectedEnd ? 'ring-2 ring-blue-400 ring-offset-1' : ''} ${isSelectedStart ? 'ring-2 ring-airbnb-pink bg-airbnb-light-pink font-bold' : ''} ${isSelectedEnd ? 'ring-2 ring-emerald-500 bg-emerald-50 font-bold' : ''} ${isInRange ? 'bg-pink-50' : ''} ${info?.type === 'booking' ? 'cursor-pointer hover:scale-105' : 'cursor-crosshair'}`}
                  >
                    <span className="font-medium">{day}</span>
                    {info?.type === 'booking' && (
                      <span className="text-[10px] lg:text-xs leading-tight truncate w-full px-0.5 font-medium">{getSurname(info.guestName)}</span>
                    )}
                  </button>
                </TooltipTrigger>
                {info?.type === 'booking' ? (
                  <TooltipContent>
                    <div className="text-xs">
                      <p className="font-bold">{info.guestName}</p>
                      <p className="text-gray-400">{formatDate(info.checkIn)} - {formatDate(info.checkOut)}</p>
                      <p className="capitalize mt-1 font-medium">{info.status}</p>
                      <p className="capitalize text-gray-400">via {info.channel}</p>
                      <p className="mt-2 text-blue-500 font-medium">Double-click to view/edit</p>
                    </div>
                  </TooltipContent>
                ) : (
                  <TooltipContent>
                    <div className="text-xs">
                      <p className="font-medium">{dateStr}</p>
                      {info?.status === 'blocked' ? (
                        <p className="text-gray-400">Blocked</p>
                      ) : (
                        <p className="text-airbnb-pink">Click to select for new booking</p>
                      )}
                    </div>
                  </TooltipContent>
                )}
              </Tooltip>
            );
          })}
        </TooltipProvider>
      </div>
    </div>
  );

  const handleDateClickDual = async (yr, mo, day) => {
    if (!selectedProperty) return;
    const dateStr = `${yr}-${String(mo + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const info = dateMap[dateStr];
    
    // If clicking on a booked date, don't do anything (double-click to view)
    if (info?.type === 'booking') return;
    
    // Handle date range selection for new booking
    handleDateRangeSelect(dateStr);
  };

  const handleDateClick = async (day) => {
    if (!selectedProperty) return;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const info = dateMap[dateStr];
    
    // If clicking on a booked date, don't do anything (double-click to view)
    if (info?.type === 'booking') return;
    
    // Handle date range selection for new booking
    handleDateRangeSelect(dateStr);
  };

  // Handle date range selection for creating new booking
  const handleDateRangeSelect = (dateStr) => {
    const clickedDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Prevent selecting past dates for check-in
    if (clickedDate < today) {
      toast.error('Cannot create bookings for past dates');
      return;
    }
    
    if (!dateRangeSelection.start) {
      // First click - set arrival date
      setDateRangeSelection({ start: dateStr, end: null });
      toast.info('Check-in date selected. Click on check-out date.');
    } else if (!dateRangeSelection.end) {
      // Second click - set departure date
      const startDate = new Date(dateRangeSelection.start);
      
      if (clickedDate <= startDate) {
        // If clicked date is same or before start, reset and start over
        if (clickedDate < today) {
          toast.error('Cannot create bookings for past dates');
          setDateRangeSelection({ start: null, end: null });
          return;
        }
        setDateRangeSelection({ start: dateStr, end: null });
        toast.info('Check-in date selected. Click on check-out date.');
        return;
      }
      
      // Check if any dates in range are blocked or booked
      const rangeHasConflict = checkDateRangeConflict(dateRangeSelection.start, dateStr);
      if (rangeHasConflict) {
        toast.error('Some dates in this range are blocked or already booked.');
        setDateRangeSelection({ start: null, end: null });
        return;
      }
      
      // Valid range - open new booking dialog
      setDateRangeSelection({ ...dateRangeSelection, end: dateStr });
      setNewBookingForm({
        guest_name: '',
        guest_email: '',
        guest_phone: '',
        guests: 1,
        channel: 'direct',
        status: 'provisional',
        total_amount: 0,
        notes: ''
      });
      setNewBookingDialogOpen(true);
    } else {
      // Already have both dates, start new selection
      if (clickedDate < today) {
        toast.error('Cannot create bookings for past dates');
        return;
      }
      setDateRangeSelection({ start: dateStr, end: null });
      toast.info('Check-in date selected. Click on check-out date.');
    }
  };

  // Check if date range has conflicts
  const checkDateRangeConflict = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (let d = new Date(startDate); d < endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const info = dateMap[dateStr];
      if (info?.type === 'booking' || info?.status === 'blocked') {
        return true;
      }
    }
    return false;
  };

  // Clear date selection
  const clearDateSelection = () => {
    setDateRangeSelection({ start: null, end: null });
  };

  // Check if a date is in the selected range (for highlighting)
  const isDateInSelection = (dateStr) => {
    if (!dateRangeSelection.start) return false;
    const date = new Date(dateStr);
    const start = new Date(dateRangeSelection.start);
    
    if (!dateRangeSelection.end) {
      return dateStr === dateRangeSelection.start;
    }
    
    const end = new Date(dateRangeSelection.end);
    return date >= start && date <= end;
  };

  // Create new booking from date selection
  const handleCreateNewBooking = async () => {
    if (!selectedProperty || !dateRangeSelection.start || !dateRangeSelection.end) return;
    if (!newBookingForm.guest_name) {
      toast.error('Guest name is required');
      return;
    }

    setSavingNewBooking(true);
    try {
      await bookingsAPI.create({
        property_id: selectedProperty,
        check_in: dateRangeSelection.start,
        check_out: dateRangeSelection.end,
        ...newBookingForm
      });
      toast.success('Booking created successfully!');
      setNewBookingDialogOpen(false);
      setDateRangeSelection({ start: null, end: null });
      loadCalendarData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSavingNewBooking(false);
    }
  };

  const getDateStyle = (info, dateStr) => {
    // Check if date is in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const date = new Date(dateStr);
    const isPast = date < today;
    
    // Check if date is in selection range
    const isSelected = isDateInSelection(dateStr);
    const selectionClass = isSelected ? 'ring-2 ring-airbnb-pink ring-offset-1' : '';
    
    // Past dates styling (unless they have a booking)
    if (isPast && (!info || info.type !== 'booking')) {
      return 'bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed';
    }
    
    if (!info) return `bg-white text-airbnb-gray hover:bg-gray-50 border-gray-200 ${selectionClass}`;
    if (info.type === 'booking') {
      switch (info.status) {
        case 'confirmed': 
        case 'paid':
          return 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 font-semibold';
        case 'provisional':
          return 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 font-medium';
        case 'waitlist':
          return 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100 font-medium';
        case 'checked-in': 
          return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 font-semibold';
        case 'checked-out':
          return 'bg-gray-100 text-gray-500 border-gray-200';
        default: 
          return 'bg-gray-50 text-gray-600 border-gray-200';
      }
    }
    if (info.status === 'blocked') return 'bg-gray-100 text-gray-400 border-gray-200 line-through';
    return `bg-white text-airbnb-gray hover:bg-gray-50 border-gray-200 ${selectionClass}`;
  };

  const getSurname = (name) => {
    if (!name) return '';
    const parts = name.trim().split(' ');
    return parts.length > 1 ? parts[parts.length - 1] : name;
  };

  // iCal functions
  const handleExportICal = () => {
    if (!selectedProperty) return;
    const token = localStorage.getItem('ihostpms_token');
    window.open(`${BACKEND_URL}/api/ical/export/${selectedProperty}?token=${token}`, '_blank');
  };

  const handleGenerateFeed = async () => {
    if (!selectedProperty) return;
    setFeedLoading(true);
    try {
      const token = localStorage.getItem('ihostpms_token');
      const resp = await fetch(`${BACKEND_URL}/api/ical/feed/${selectedProperty}/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await resp.json();
      if (data.feed_token) {
        setFeedUrl(`${BACKEND_URL}/api/ical/feed/${selectedProperty}/${data.feed_token}`);
      }
    } catch (err) {
      console.error('Feed gen error:', err);
    } finally {
      setFeedLoading(false);
    }
  };

  const handleLoadFeedInfo = async () => {
    if (!selectedProperty) return;
    try {
      const token = localStorage.getItem('ihostpms_token');
      const resp = await fetch(`${BACKEND_URL}/api/ical/feed/${selectedProperty}/info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!resp.ok) {
        setFeedUrl('');
        return;
      }
      const data = await resp.json();
      if (data.feed_token) {
        setFeedUrl(`${BACKEND_URL}/api/ical/feed/${selectedProperty}/${data.feed_token}`);
      } else {
        setFeedUrl('');
      }
    } catch (err) {
      console.error('Feed info error:', err);
      setFeedUrl('');
    }
  };

  const handleCopyFeed = () => {
    navigator.clipboard.writeText(feedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleImportICal = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProperty) return;
    setImportResult(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const token = localStorage.getItem('ihostpms_token');
      const resp = await fetch(`${BACKEND_URL}/api/ical/import/${selectedProperty}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await resp.json();
      setImportResult(data);
      loadCalendarData();
    } catch (err) {
      setImportResult({ message: 'Import failed', error: true });
    }
    e.target.value = '';
  };

  const loadSubscriptions = useCallback(async () => {
    try {
      const resp = await icalSyncAPI.listSubscriptions();
      setSubscriptions(resp.data);
    } catch (err) {
      console.error('Sub load error:', err);
    }
  }, []);

  useEffect(() => { loadSubscriptions(); }, [loadSubscriptions]);

  const handleAddSubscription = async () => {
    if (!newFeedUrl || !selectedProperty) return;
    setSubLoading(true);
    try {
      await icalSyncAPI.createSubscription({
        property_id: selectedProperty,
        feed_url: newFeedUrl,
        source_name: newSourceName || 'External',
        poll_interval_minutes: newSyncInterval,
      });
      setNewFeedUrl('');
      setNewSourceName('');
      setNewSyncInterval(60);
      loadSubscriptions();
      toast.success('iCal subscription added', { 
        description: `Will sync every ${newSyncInterval} minutes` 
      });
    } catch (err) {
      console.error('Add sub error:', err);
      toast.error('Failed to add subscription', { 
        description: err.response?.data?.detail || 'Please check the feed URL' 
      });
    } finally {
      setSubLoading(false);
    }
  };

  const handleDeleteSubscription = async (id) => {
    try {
      await icalSyncAPI.deleteSubscription(id);
      loadSubscriptions();
      toast.success('Subscription removed');
    } catch (err) {
      console.error('Delete sub error:', err);
      toast.error('Failed to remove subscription');
    }
  };

  const handleSyncNow = async (id) => {
    setSyncingId(id);
    const sub = subscriptions.find(s => s.subscription_id === id);
    toast.info('Sync started', { description: `Syncing ${sub?.source_name || 'calendar'}...` });
    try {
      await icalSyncAPI.syncNow(id);
      setTimeout(() => { 
        loadSubscriptions(); 
        setSyncingId(null); 
        toast.success('Sync completed', { 
          description: `${sub?.source_name || 'Calendar'} synced successfully` 
        });
      }, 2000);
    } catch (err) {
      console.error('Sync error:', err);
      setSyncingId(null);
      toast.error('Sync failed', { 
        description: err.response?.data?.detail || 'Could not sync calendar' 
      });
    }
  };

  const handleEditInterval = (sub) => {
    setEditingSubId(sub.subscription_id);
    setEditInterval(sub.poll_interval_minutes || 60);
  };

  const handleSaveInterval = async (subId) => {
    try {
      await icalSyncAPI.updateSubscription(subId, { poll_interval_minutes: editInterval });
      setEditingSubId(null);
      loadSubscriptions();
      toast.success('Sync interval updated', { 
        description: `Will now sync every ${editInterval} minutes` 
      });
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Failed to update interval');
    }
  };

  if (loadingProps) return <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" /></div>;

  return (
    <div data-testid="calendar-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-airbnb-gray">Availability Calendar</h1>
          <p className="text-airbnb-medium-gray mt-1">Manage availability and view bookings</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadCalendarData} disabled={loadingCal} className="rounded-xl" data-testid="calendar-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingCal ? 'animate-spin' : ''}`} /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={() => { setIcalDialogOpen(true); handleLoadFeedInfo(); }} className="rounded-xl" data-testid="ical-btn">
          <LinkIcon className="w-4 h-4 mr-2" /> iCal Sync
        </Button>
      </div>

      {/* Today's Overview Panel */}
      {(todaysOverview.checkIns.length > 0 || todaysOverview.checkOuts.length > 0 || todaysOverview.pendingPayments.length > 0) && (
        <div className="bg-white rounded-2xl border border-gray-100 mb-6 overflow-hidden" data-testid="todays-overview">
          <button 
            onClick={() => setOverviewExpanded(!overviewExpanded)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-airbnb-pink to-rose-500 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <h2 className="text-lg font-bold text-airbnb-gray">Today's Overview</h2>
                <p className="text-xs text-airbnb-medium-gray">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {todaysOverview.checkIns.length > 0 && (
                <span className="flex items-center gap-1.5 text-sm text-emerald-600 font-medium">
                  <LogIn className="w-4 h-4" /> {todaysOverview.checkIns.length} Check-in{todaysOverview.checkIns.length > 1 ? 's' : ''}
                </span>
              )}
              {todaysOverview.checkOuts.length > 0 && (
                <span className="flex items-center gap-1.5 text-sm text-blue-600 font-medium">
                  <LogOut className="w-4 h-4" /> {todaysOverview.checkOuts.length} Check-out{todaysOverview.checkOuts.length > 1 ? 's' : ''}
                </span>
              )}
              {todaysOverview.pendingPayments.length > 0 && (
                <span className="flex items-center gap-1.5 text-sm text-amber-600 font-medium">
                  <Banknote className="w-4 h-4" /> {todaysOverview.pendingPayments.length} Pending
                </span>
              )}
              {overviewExpanded ? <ChevronUp className="w-5 h-5 text-airbnb-medium-gray" /> : <ChevronDown className="w-5 h-5 text-airbnb-medium-gray" />}
            </div>
          </button>
          
          {overviewExpanded && (
            <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Today's Check-ins */}
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                <div className="flex items-center gap-2 mb-3">
                  <LogIn className="w-5 h-5 text-emerald-600" />
                  <h3 className="font-semibold text-emerald-800">Check-ins Today</h3>
                </div>
                {todaysOverview.checkIns.length === 0 ? (
                  <p className="text-sm text-emerald-600/70">No check-ins scheduled</p>
                ) : (
                  <div className="space-y-2">
                    {todaysOverview.checkIns.slice(0, 3).map(b => (
                      <button
                        key={b.booking_id}
                        onClick={() => handleBookingDoubleClick(b.booking_id)}
                        className="w-full text-left p-2 bg-white rounded-lg hover:bg-emerald-100 transition-colors"
                        data-testid={`checkin-${b.booking_id}`}
                      >
                        <p className="font-medium text-sm text-airbnb-gray truncate">{b.guest_name}</p>
                        <p className="text-xs text-airbnb-medium-gray truncate">{b.property_name}</p>
                      </button>
                    ))}
                    {todaysOverview.checkIns.length > 3 && (
                      <p className="text-xs text-emerald-600 text-center pt-1">+{todaysOverview.checkIns.length - 3} more</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Today's Check-outs */}
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-3">
                  <LogOut className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-blue-800">Check-outs Today</h3>
                </div>
                {todaysOverview.checkOuts.length === 0 ? (
                  <p className="text-sm text-blue-600/70">No check-outs scheduled</p>
                ) : (
                  <div className="space-y-2">
                    {todaysOverview.checkOuts.slice(0, 3).map(b => (
                      <button
                        key={b.booking_id}
                        onClick={() => handleBookingDoubleClick(b.booking_id)}
                        className="w-full text-left p-2 bg-white rounded-lg hover:bg-blue-100 transition-colors"
                        data-testid={`checkout-${b.booking_id}`}
                      >
                        <p className="font-medium text-sm text-airbnb-gray truncate">{b.guest_name}</p>
                        <p className="text-xs text-airbnb-medium-gray truncate">{b.property_name}</p>
                      </button>
                    ))}
                    {todaysOverview.checkOuts.length > 3 && (
                      <p className="text-xs text-blue-600 text-center pt-1">+{todaysOverview.checkOuts.length - 3} more</p>
                    )}
                  </div>
                )}
              </div>
              
              {/* Pending Payments */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <div className="flex items-center gap-2 mb-3">
                  <Banknote className="w-5 h-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-800">Pending Payments</h3>
                </div>
                {todaysOverview.pendingPayments.length === 0 ? (
                  <p className="text-sm text-amber-600/70">All bookings paid</p>
                ) : (
                  <div className="space-y-2">
                    {todaysOverview.pendingPayments.slice(0, 3).map(b => (
                      <button
                        key={b.booking_id}
                        onClick={() => handleBookingDoubleClick(b.booking_id)}
                        className="w-full text-left p-2 bg-white rounded-lg hover:bg-amber-100 transition-colors"
                        data-testid={`pending-${b.booking_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-sm text-airbnb-gray truncate">{b.guest_name}</p>
                          <span className="text-xs font-semibold text-amber-700">{b.currency || 'USD'} {b.total_price?.toLocaleString()}</span>
                        </div>
                        <p className="text-xs text-airbnb-medium-gray">{formatDate(b.check_in)} - {formatDate(b.check_out)}</p>
                      </button>
                    ))}
                    {todaysOverview.pendingPayments.length > 3 && (
                      <p className="text-xs text-amber-600 text-center pt-1">+{todaysOverview.pendingPayments.length - 3} more</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        {/* Property selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-airbnb-gray mb-2">Property</label>
          <div className="flex flex-wrap items-center gap-4">
            <select
              data-testid="calendar-property-select"
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
              className="w-full md:w-72 h-10 px-3 border border-gray-200 rounded-xl text-sm bg-white focus:border-airbnb-pink focus:ring-1 focus:ring-airbnb-pink/20"
            >
              {properties.map(p => <option key={p.property_id} value={p.property_id}>{p.name}</option>)}
            </select>
            
            {/* iCal Sync Status Indicator */}
            {selectedProperty && (() => {
              const propertySubs = subscriptions.filter(s => s.property_id === selectedProperty);
              const lastSync = propertySubs.reduce((latest, s) => {
                if (!s.last_sync) return latest;
                return !latest || new Date(s.last_sync) > new Date(latest) ? s.last_sync : latest;
              }, null);
              const hasError = propertySubs.some(s => s.last_sync_status === 'error');
              
              if (propertySubs.length === 0) return null;
              
              return (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-xl text-xs" data-testid="ical-sync-status">
                  {hasError ? (
                    <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                  ) : lastSync ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  ) : (
                    <Clock className="w-3.5 h-3.5 text-amber-500" />
                  )}
                  <span className="text-gray-600">
                    iCal: {propertySubs.length} feed{propertySubs.length > 1 ? 's' : ''}
                    {lastSync && (
                      <span className="ml-1 text-gray-400">
                        • Last sync: {new Date(lastSync).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                    {!lastSync && <span className="ml-1 text-amber-500">• Never synced</span>}
                  </span>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Month nav */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(year, month - 1, 1))} className="rounded-xl" data-testid="prev-month">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentDate(new Date())} 
              className="rounded-xl text-airbnb-pink border-airbnb-pink/30 hover:bg-airbnb-pink/5" 
              data-testid="today-btn"
            >
              Today
            </Button>
          </div>
          <h3 className="text-lg font-bold text-airbnb-gray lg:hidden" data-testid="calendar-month-label">{monthName} {year}</h3>
          <h3 className="text-lg font-bold text-airbnb-gray hidden lg:block" data-testid="calendar-month-label-dual">{monthName} - {nextMonthName} {year === nextYear ? year : `${year}/${nextYear}`}</h3>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date(year, month + 1, 1))} className="rounded-xl" data-testid="next-month">
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Date Selection Indicator */}
        {(dateRangeSelection.start || dateRangeSelection.end) && (
          <div className="bg-airbnb-light-pink rounded-lg p-3 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-airbnb-pink" />
                <span className="text-sm font-medium text-airbnb-gray">
                  {dateRangeSelection.start ? (
                    <>Check-in: <strong>{formatDate(dateRangeSelection.start)}</strong></>
                  ) : (
                    <span className="text-airbnb-medium-gray">Click a date to select check-in</span>
                  )}
                </span>
              </div>
              {dateRangeSelection.start && (
                <div className="flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-airbnb-medium-gray" />
                  <div className="w-3 h-3 rounded-full bg-emerald-500" />
                  <span className="text-sm font-medium text-airbnb-gray">
                    {dateRangeSelection.end ? (
                      <>Check-out: <strong>{formatDate(dateRangeSelection.end)}</strong></>
                    ) : (
                      <span className="text-airbnb-medium-gray">Click a date to select check-out</span>
                    )}
                  </span>
                </div>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearDateSelection} 
              className="text-airbnb-pink hover:text-airbnb-dark-pink hover:bg-airbnb-light-pink"
              data-testid="clear-date-selection"
            >
              <XCircle className="w-4 h-4 mr-1" /> Clear
            </Button>
          </div>
        )}

        {/* Tip for adding bookings - show when no date selection */}
        {!dateRangeSelection.start && !dateRangeSelection.end && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Plus className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-blue-800">Quick Tip: Add a New Booking</p>
              <p className="text-xs text-blue-600">Click on an arrival date, then click on a departure date to create a new booking.</p>
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="flex flex-wrap gap-3 lg:gap-4 mb-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-emerald-500" /><span className="text-[10px] lg:text-xs text-airbnb-medium-gray">Confirmed</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-amber-400" /><span className="text-[10px] lg:text-xs text-airbnb-medium-gray">Provisional</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-purple-500" /><span className="text-[10px] lg:text-xs text-airbnb-medium-gray">Waitlist</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-blue-500" /><span className="text-[10px] lg:text-xs text-airbnb-medium-gray">Checked-in</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-gray-300" /><span className="text-[10px] lg:text-xs text-airbnb-medium-gray">Blocked</span></div>
          <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-white border border-gray-300" /><span className="text-[10px] lg:text-xs text-airbnb-medium-gray">Available</span></div>
        </div>

        {/* Calendar grid - dual month view on desktop */}
        {loadingCal ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 text-airbnb-pink animate-spin" /></div>
        ) : (
          <>
            {/* Mobile: Single month view */}
            <div className="lg:hidden">
              <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                  <div key={d} className="text-center text-[10px] font-semibold text-airbnb-medium-gray uppercase py-1">{d}</div>
                ))}
                {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} className="aspect-square" />)}
                <TooltipProvider>
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const info = dateMap[dateStr];
                    const isToday = dateStr === new Date().toISOString().split('T')[0];
                    const isSelectedStart = dateRangeSelection.start === dateStr;
                    const isSelectedEnd = dateRangeSelection.end === dateStr;
                    return (
                      <Tooltip key={day}>
                        <TooltipTrigger asChild>
                          <button
                            data-testid={`calendar-day-${day}`}
                            onClick={() => handleDateClick(day)}
                            onDoubleClick={() => info?.type === 'booking' && handleBookingDoubleClick(info.bookingId)}
                            className={`aspect-square flex flex-col items-center justify-center rounded-lg border text-sm transition-all ${getDateStyle(info, dateStr)} ${isToday && !isSelectedStart && !isSelectedEnd ? 'ring-2 ring-blue-400 ring-offset-1' : ''} ${isSelectedStart ? 'ring-2 ring-airbnb-pink bg-airbnb-light-pink' : ''} ${isSelectedEnd ? 'ring-2 ring-emerald-500 bg-emerald-50' : ''} ${info?.type === 'booking' ? 'cursor-pointer' : 'cursor-crosshair'}`}
                          >
                            <span className="font-medium">{day}</span>
                            {info?.type === 'booking' && (
                              <span className="text-[10px] leading-tight truncate w-full px-0.5 font-medium">{getSurname(info.guestName)}</span>
                            )}
                          </button>
                        </TooltipTrigger>
                        {info?.type === 'booking' && (
                          <TooltipContent>
                            <div className="text-xs">
                              <p className="font-bold">{info.guestName}</p>
                              <p className="text-gray-400">{formatDate(info.checkIn)} - {formatDate(info.checkOut)}</p>
                              <p className="capitalize mt-1 font-medium">{info.status}</p>
                              <p className="capitalize text-gray-400">via {info.channel}</p>
                              <p className="mt-2 text-blue-500 font-medium">Double-click to edit</p>
                            </div>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    );
                  })}
                </TooltipProvider>
              </div>
            </div>

            {/* Desktop: Dual month view */}
            <div className="hidden lg:flex lg:gap-6">
              {renderMonth(year, month, monthName, firstDay, daysInMonth)}
              <div className="w-px bg-gray-200" />
              {renderMonth(nextYear, nextMonth, nextMonthName, nextFirstDay, nextDaysInMonth)}
            </div>
          </>
        )}
      </div>

      {/* iCal Sync Dialog */}
      <Dialog open={icalDialogOpen} onOpenChange={setIcalDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><LinkIcon className="w-5 h-5 text-airbnb-pink" /> iCal Calendar Sync</DialogTitle>
            <DialogDescription className="sr-only">Manage iCal calendar synchronization</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Export */}
            <div>
              <h4 className="text-sm font-bold text-airbnb-gray mb-2">Export Calendar</h4>
              <p className="text-xs text-airbnb-medium-gray mb-3">Download .ics file with all bookings and blocked dates for this property.</p>
              <Button onClick={handleExportICal} variant="outline" size="sm" className="rounded-xl w-full" data-testid="export-ical-btn">
                <Download className="w-4 h-4 mr-2" /> Download .ics File
              </Button>
            </div>

            {/* Public Feed URL */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-sm font-bold text-airbnb-gray mb-2">Public Feed URL</h4>
              <p className="text-xs text-airbnb-medium-gray mb-3">Share this URL with Airbnb, Booking.com, or other platforms to auto-sync your calendar.</p>
              {feedUrl ? (
                <div className="flex gap-2">
                  <input value={feedUrl} readOnly className="flex-1 h-9 px-3 text-xs border border-gray-200 rounded-lg bg-gray-50 truncate" data-testid="feed-url-input" />
                  <Button onClick={handleCopyFeed} size="sm" variant="outline" className="rounded-xl" data-testid="copy-feed-btn">
                    {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              ) : (
                <Button onClick={handleGenerateFeed} disabled={feedLoading} size="sm" className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl w-full" data-testid="generate-feed-btn">
                  {feedLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LinkIcon className="w-4 h-4 mr-2" />} Generate Feed URL
                </Button>
              )}
            </div>

            {/* Import */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-sm font-bold text-airbnb-gray mb-2">Import Calendar</h4>
              <p className="text-xs text-airbnb-medium-gray mb-3">Import .ics file from another platform to sync bookings and blocked dates.</p>
              <input type="file" accept=".ics,.ical" ref={fileInputRef} onChange={handleImportICal} className="hidden" />
              <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="rounded-xl w-full" data-testid="import-ical-btn">
                <Upload className="w-4 h-4 mr-2" /> Import .ics File
              </Button>
              {importResult && (
                <div className={`mt-2 p-2 rounded-lg text-xs ${importResult.error ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-700'}`}>
                  {importResult.message || `Imported: ${importResult.bookings_imported} bookings, ${importResult.dates_blocked} blocked dates`}
                </div>
              )}
            </div>

            {/* Auto-Sync Subscriptions */}
            <div className="border-t border-gray-100 pt-4">
              <h4 className="text-sm font-bold text-airbnb-gray mb-2">Auto-Sync Subscriptions</h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
                <p className="text-xs text-blue-700">
                  <strong>Sync Frequency:</strong> Choose how often feeds are synced (15, 30, or 60 minutes). 
                  Use "Sync Now" to trigger immediate sync.
                </p>
              </div>

              {/* Add new subscription */}
              <div className="flex flex-col gap-2 mb-4">
                <Input
                  data-testid="new-feed-url"
                  placeholder="https://www.airbnb.com/calendar/ical/..."
                  value={newFeedUrl}
                  onChange={(e) => setNewFeedUrl(e.target.value)}
                  className="text-xs h-9"
                />
                <div className="flex gap-2">
                  <Input
                    data-testid="new-feed-source"
                    placeholder="Source name (e.g., Airbnb)"
                    value={newSourceName}
                    onChange={(e) => setNewSourceName(e.target.value)}
                    className="text-xs h-9 flex-1"
                  />
                  <select
                    data-testid="new-sync-interval"
                    value={newSyncInterval}
                    onChange={(e) => setNewSyncInterval(Number(e.target.value))}
                    className="text-xs h-9 px-2 border border-gray-200 rounded-xl bg-white"
                  >
                    <option value={15}>15 min</option>
                    <option value={30}>30 min</option>
                    <option value={60}>60 min</option>
                  </select>
                  <Button
                    data-testid="add-subscription-btn"
                    onClick={handleAddSubscription}
                    disabled={!newFeedUrl || subLoading}
                    size="sm"
                    className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl h-9 px-4"
                  >
                    {subLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* List subscriptions */}
              {subscriptions.filter(s => s.property_id === selectedProperty).length === 0 ? (
                <p className="text-xs text-airbnb-medium-gray text-center py-3">No auto-sync subscriptions for this property</p>
              ) : (
                <div className="space-y-2">
                  {subscriptions.filter(s => s.property_id === selectedProperty).map((sub) => (
                    <div key={sub.subscription_id} className="bg-gray-50 rounded-xl p-3" data-testid={`sub-${sub.subscription_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-semibold text-airbnb-gray">{sub.source_name}</span>
                            {sub.last_sync_status === 'success' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                            {sub.last_sync_status === 'error' && <AlertCircle className="w-3.5 h-3.5 text-red-500" />}
                            {editingSubId === sub.subscription_id ? (
                              <div className="flex items-center gap-1">
                                <select
                                  value={editInterval}
                                  onChange={(e) => setEditInterval(Number(e.target.value))}
                                  className="text-[10px] h-5 px-1 border border-gray-300 rounded bg-white"
                                  data-testid={`edit-interval-${sub.subscription_id}`}
                                >
                                  <option value={15}>15 min</option>
                                  <option value={30}>30 min</option>
                                  <option value={60}>60 min</option>
                                </select>
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-5 w-5 p-0 text-emerald-600"
                                  onClick={() => handleSaveInterval(sub.subscription_id)}
                                  data-testid={`save-interval-${sub.subscription_id}`}
                                >
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost" size="sm"
                                  className="h-5 w-5 p-0 text-gray-400"
                                  onClick={() => setEditingSubId(null)}
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleEditInterval(sub)}
                                className="text-[10px] text-gray-400 px-1.5 py-0.5 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors cursor-pointer"
                                data-testid={`interval-btn-${sub.subscription_id}`}
                              >
                                {sub.poll_interval_minutes || 60} min ✏️
                              </button>
                            )}
                          </div>
                          <p className="text-[10px] text-airbnb-medium-gray truncate mt-0.5">{sub.feed_url}</p>
                          {sub.last_sync && (
                            <p className="text-[10px] text-airbnb-medium-gray mt-0.5 flex items-center gap-1">
                              <Clock className="w-3 h-3" /> Last sync: {new Date(sub.last_sync).toLocaleString()}
                              {sub.last_sync_events > 0 && ` (${sub.last_sync_events} events)`}
                            </p>
                          )}
                          {sub.sync_error && (
                            <p className="text-[10px] text-red-500 mt-0.5 truncate">{sub.sync_error}</p>
                          )}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button
                            variant="ghost" size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => handleSyncNow(sub.subscription_id)}
                            disabled={syncingId === sub.subscription_id}
                            data-testid={`sync-now-${sub.subscription_id}`}
                          >
                            <RotateCw className={`w-3.5 h-3.5 ${syncingId === sub.subscription_id ? 'animate-spin' : ''}`} />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                            onClick={() => handleDeleteSubscription(sub.subscription_id)}
                            data-testid={`delete-sub-${sub.subscription_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Booking Detail Dialog */}
      <Dialog open={bookingDialogOpen} onOpenChange={(open) => { if (!open) { setBookingDialogOpen(false); setEditMode(false); setSelectedBooking(null); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-airbnb-pink" />
              Booking Details
            </DialogTitle>
            <DialogDescription>View and manage booking information</DialogDescription>
          </DialogHeader>
          
          {selectedBooking && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${statusStyles[selectedBooking.status] || 'bg-gray-100 text-gray-600'}`}>
                  {selectedBooking.status}
                </span>
                <span className="text-xs text-airbnb-medium-gray">ID: {selectedBooking.booking_id}</span>
              </div>

              {/* Guest Info */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <h4 className="text-sm font-semibold text-airbnb-gray flex items-center gap-2">
                  <User className="w-4 h-4" /> Guest Information
                </h4>
                {editMode ? (
                  <div className="space-y-2">
                    <Input
                      value={bookingForm.guest_name || ''}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_name: e.target.value })}
                      placeholder="Guest Name"
                      className="text-sm"
                    />
                    <Input
                      value={bookingForm.guest_email || ''}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_email: e.target.value })}
                      placeholder="Email"
                      type="email"
                      className="text-sm"
                    />
                    <Input
                      value={bookingForm.guest_phone || ''}
                      onChange={(e) => setBookingForm({ ...bookingForm, guest_phone: e.target.value })}
                      placeholder="Phone"
                      className="text-sm"
                    />
                  </div>
                ) : (
                  <div className="space-y-1 text-sm">
                    <p className="font-medium text-airbnb-gray">{selectedBooking.guest_name}</p>
                    {selectedBooking.guest_email && (
                      <p className="text-airbnb-medium-gray flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> {selectedBooking.guest_email}
                      </p>
                    )}
                    {selectedBooking.guest_phone && (
                      <p className="text-airbnb-medium-gray flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5" /> {selectedBooking.guest_phone}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Dates & Details */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <h4 className="text-sm font-semibold text-airbnb-gray flex items-center gap-2">
                  <Calendar className="w-4 h-4" /> Stay Details
                </h4>
                {editMode ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-airbnb-medium-gray">Check-in</Label>
                      <Input
                        type="date"
                        value={bookingForm.check_in || ''}
                        onChange={(e) => setBookingForm({ ...bookingForm, check_in: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-airbnb-medium-gray">Check-out</Label>
                      <Input
                        type="date"
                        value={bookingForm.check_out || ''}
                        onChange={(e) => setBookingForm({ ...bookingForm, check_out: e.target.value })}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-airbnb-medium-gray">Guests</Label>
                      <Input
                        type="number"
                        min="1"
                        value={bookingForm.num_guests || 1}
                        onChange={(e) => setBookingForm({ ...bookingForm, num_guests: parseInt(e.target.value) || 1 })}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-airbnb-medium-gray">Channel</Label>
                      <Select value={bookingForm.channel || 'direct'} onValueChange={(val) => setBookingForm({ ...bookingForm, channel: val })}>
                        <SelectTrigger className="text-sm h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CHANNELS.map(ch => <SelectItem key={ch} value={ch} className="capitalize">{ch}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div>
                      <p className="text-xs text-airbnb-medium-gray">Check-in</p>
                      <p className="font-medium text-airbnb-gray">{formatDate(selectedBooking.check_in)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-airbnb-medium-gray">Check-out</p>
                      <p className="font-medium text-airbnb-gray">{formatDate(selectedBooking.check_out)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-airbnb-medium-gray">Guests</p>
                      <p className="font-medium text-airbnb-gray">{selectedBooking.num_guests}</p>
                    </div>
                    <div>
                      <p className="text-xs text-airbnb-medium-gray">Channel</p>
                      <p className="font-medium text-airbnb-gray capitalize">{selectedBooking.channel}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-airbnb-gray flex items-center gap-2 mb-2">
                  <DollarSign className="w-4 h-4" /> Pricing
                </h4>
                {editMode ? (
                  <div>
                    <Label className="text-xs text-airbnb-medium-gray">Total Price</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={bookingForm.total_price || 0}
                      onChange={(e) => setBookingForm({ ...bookingForm, total_price: parseFloat(e.target.value) || 0 })}
                      className="text-sm"
                    />
                  </div>
                ) : (
                  <p className="text-lg font-bold text-airbnb-pink">{selectedBooking.currency || 'USD'} {selectedBooking.total_price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                )}
              </div>

              {/* Notes */}
              {editMode && (
                <div>
                  <Label className="text-xs text-airbnb-medium-gray">Notes</Label>
                  <Textarea
                    value={bookingForm.notes || ''}
                    onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                    placeholder="Internal notes..."
                    rows={2}
                    className="text-sm"
                  />
                </div>
              )}
              {!editMode && selectedBooking.notes && (
                <div className="text-sm text-airbnb-medium-gray bg-gray-50 rounded-xl p-3">
                  <p className="text-xs font-semibold text-airbnb-gray mb-1">Notes</p>
                  {selectedBooking.notes}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                {editMode ? (
                  <>
                    <Button
                      onClick={handleBookingUpdate}
                      disabled={savingBooking}
                      className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl"
                      data-testid="save-booking-btn"
                    >
                      {savingBooking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => setEditMode(false)} className="rounded-xl" data-testid="cancel-edit-btn">
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setEditMode(true)} className="rounded-xl" data-testid="edit-booking-btn">
                      <Edit className="w-4 h-4 mr-2" /> Edit
                    </Button>
                    {selectedBooking.status !== 'confirmed' && selectedBooking.status !== 'paid' && selectedBooking.status !== 'checked-in' && (
                      <Button
                        variant="outline"
                        onClick={() => setPaymentDialogOpen(true)}
                        className="rounded-xl text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                        data-testid="record-payment-btn"
                      >
                        <CreditCard className="w-4 h-4 mr-2" /> Record Payment
                      </Button>
                    )}
                    {selectedBooking.status !== 'cancelled' && selectedBooking.status !== 'checked-in' && selectedBooking.status !== 'checked-out' && (
                      <Button
                        variant="outline"
                        onClick={() => setCancelDialogOpen(true)}
                        className="rounded-xl text-red-600 border-red-200 hover:bg-red-50"
                        data-testid="cancel-booking-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" /> Cancel Booking
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Record Manual Payment
            </DialogTitle>
            <DialogDescription>Record a payment received outside the system</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-airbnb-gray">Payment Method</Label>
              <Select value={paymentForm.payment_method} onValueChange={(val) => setPaymentForm({ ...paymentForm, payment_method: val })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map(pm => (
                    <SelectItem key={pm.value} value={pm.value}>{pm.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-sm text-airbnb-gray">Reference / Transaction ID (Optional)</Label>
              <Input
                value={paymentForm.payment_ref}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_ref: e.target.value })}
                placeholder="e.g., Bank transfer ref, M-Pesa code..."
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-sm text-airbnb-gray">Notes (Optional)</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleRecordPayment}
              disabled={recordingPayment}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
              data-testid="confirm-payment-btn"
            >
              {recordingPayment ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Cancel Booking?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The booking will be marked as cancelled and the guest will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="my-4">
            <Label className="text-sm text-airbnb-gray">Cancellation Reason (Optional)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation..."
              rows={2}
              className="mt-1"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="cancel-dialog-cancel">
              Keep Booking
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelBooking}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Cancel Booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New Booking from Date Selection Dialog */}
      <Dialog open={newBookingDialogOpen} onOpenChange={(open) => { 
        setNewBookingDialogOpen(open); 
        if (!open) clearDateSelection(); 
      }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-airbnb-pink" />
              New Booking
            </DialogTitle>
            <DialogDescription>
              Create a booking for the selected dates
            </DialogDescription>
          </DialogHeader>

          {/* Selected Dates Display */}
          <div className="bg-airbnb-light-pink rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <p className="text-xs text-airbnb-medium-gray uppercase font-medium">Check-in</p>
                <p className="text-lg font-bold text-airbnb-gray">{dateRangeSelection.start ? formatDate(dateRangeSelection.start) : '-'}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-airbnb-medium-gray" />
              <div className="text-center flex-1">
                <p className="text-xs text-airbnb-medium-gray uppercase font-medium">Check-out</p>
                <p className="text-lg font-bold text-airbnb-gray">{dateRangeSelection.end ? formatDate(dateRangeSelection.end) : '-'}</p>
              </div>
            </div>
            {dateRangeSelection.start && dateRangeSelection.end && (
              <p className="text-center text-sm text-airbnb-pink mt-2 font-medium">
                {Math.ceil((new Date(dateRangeSelection.end) - new Date(dateRangeSelection.start)) / (1000 * 60 * 60 * 24))} nights
              </p>
            )}
          </div>

          {/* Guest Information */}
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Guest Name *</Label>
              <Input
                value={newBookingForm.guest_name}
                onChange={(e) => setNewBookingForm({ ...newBookingForm, guest_name: e.target.value })}
                placeholder="John Doe"
                className="mt-1"
                data-testid="new-booking-guest-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium text-airbnb-gray">Email</Label>
                <Input
                  type="email"
                  value={newBookingForm.guest_email}
                  onChange={(e) => setNewBookingForm({ ...newBookingForm, guest_email: e.target.value })}
                  placeholder="guest@email.com"
                  className="mt-1"
                  data-testid="new-booking-guest-email"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-airbnb-gray">Phone</Label>
                <Input
                  value={newBookingForm.guest_phone}
                  onChange={(e) => setNewBookingForm({ ...newBookingForm, guest_phone: e.target.value })}
                  placeholder="+1 234 567 890"
                  className="mt-1"
                  data-testid="new-booking-guest-phone"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium text-airbnb-gray">Guests</Label>
                <Input
                  type="number"
                  min="1"
                  value={newBookingForm.guests}
                  onChange={(e) => setNewBookingForm({ ...newBookingForm, guests: parseInt(e.target.value) || 1 })}
                  className="mt-1"
                  data-testid="new-booking-guests"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-airbnb-gray">Channel</Label>
                <Select value={newBookingForm.channel} onValueChange={(v) => setNewBookingForm({ ...newBookingForm, channel: v })}>
                  <SelectTrigger className="mt-1" data-testid="new-booking-channel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direct">Direct</SelectItem>
                    <SelectItem value="airbnb">Airbnb</SelectItem>
                    <SelectItem value="booking">Booking.com</SelectItem>
                    <SelectItem value="vrbo">VRBO</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium text-airbnb-gray">Status</Label>
                <Select value={newBookingForm.status} onValueChange={(v) => setNewBookingForm({ ...newBookingForm, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="new-booking-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="provisional">Provisional</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="waitlist">Waitlist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-airbnb-gray">Total Amount</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newBookingForm.total_amount}
                  onChange={(e) => setNewBookingForm({ ...newBookingForm, total_amount: parseFloat(e.target.value) || 0 })}
                  className="mt-1"
                  data-testid="new-booking-amount"
                />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-airbnb-gray">Notes</Label>
              <Textarea
                value={newBookingForm.notes}
                onChange={(e) => setNewBookingForm({ ...newBookingForm, notes: e.target.value })}
                placeholder="Any special requests or notes..."
                rows={2}
                className="mt-1"
                data-testid="new-booking-notes"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setNewBookingDialogOpen(false); clearDateSelection(); }} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateNewBooking} 
              disabled={savingNewBooking || !newBookingForm.guest_name}
              className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl"
              data-testid="create-booking-btn"
            >
              {savingNewBooking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Create Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarPage;
